// src/utils/formatters.ts

/**
 * Formats a UTC date/time string into a more readable local format (e.g., YYYY-MM-DD, h:mm am/pm).
 * Ensures the input string is treated as UTC.
 *
 * @param utcDateTime - The UTC date/time string (ISO 8601 format preferred).
 * @returns A formatted date/time string in the user's local timezone.
 */
export function formatUTCDateTime(
	utcDateTime: string | null | undefined
): string {
	if (!utcDateTime) return "N/A";

	let date: Date;
	try {
		// Ensure the date string is parsed as UTC
		if (!utcDateTime.endsWith("Z") && !utcDateTime.match(/[+-]\d{2}:\d{2}$/)) {
			utcDateTime += "Z";
		}
		date = new Date(utcDateTime);
		if (isNaN(date.getTime())) {
			throw new Error("Invalid date string");
		}
	} catch (error) {
		console.error("Error parsing date string:", utcDateTime, error);
		return "Invalid Date";
	}

	// Use Intl API for better localization and formatting
	const dateFormatter = new Intl.DateTimeFormat(undefined, {
		// Use user's default locale
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	});

	const timeFormatter = new Intl.DateTimeFormat(undefined, {
		hour: "numeric",
		minute: "2-digit",
		hour12: true, // Use AM/PM
	});

	// Format date parts (handle potential differences in locale output, e.g., MM/DD/YYYY vs YYYY-MM-DD)
	const parts = dateFormatter.formatToParts(date);
	const year = parts.find((p) => p.type === "year")?.value || "YYYY";
	const month = parts.find((p) => p.type === "month")?.value || "MM";
	const day = parts.find((p) => p.type === "day")?.value || "DD";

	const formattedDatePart = `${year}-${month}-${day}`; // Construct consistent YYYY-MM-DD

	return `${formattedDatePart}, ${timeFormatter.format(date)}`;
}

/**
 * Animates a number value within an HTML element.
 * @param elementId - The ID of the element containing the number.
 * @param targetValue - The final value to animate to.
 * @param startValueMultiplier - Multiplier for the starting value (default: 0.9512 from original JS).
 * @param initialDelay - Initial delay between increments (default: 2ms).
 * @param fastThreshold - Difference threshold to speed up animation (default: 15).
 * @param fastDelayMultiplier - Multiplier for delay when speeding up (default: 500 / difference).
 */
export function animateValue(
	elementId: string,
	targetValue: number,
	startValueMultiplier: number = 0.9512,
	initialDelay: number = 2,
	fastThreshold: number = 15,
	fastDelayMultiplierBase: number = 500
): void {
	const element = document.getElementById(elementId);
	if (!element) {
		console.warn(`Element with ID "${elementId}" not found for animation.`);
		return;
	}

	const start = Math.floor(targetValue * startValueMultiplier);
	const finish = Math.floor(targetValue); // Ensure integer comparison
	let current = start;
	let delay = initialDelay;
	let intervalId: number | null = null;

	const step = () => {
		if (current >= finish) {
			if (intervalId) clearInterval(intervalId);
			element.innerHTML = String(finish); // Ensure final value is exact
			return;
		}

		current++;
		element.innerHTML = String(current);

		const difference = finish - current;
		if (difference > 0 && difference < fastThreshold) {
			if (intervalId) clearInterval(intervalId);
			delay = fastDelayMultiplierBase / difference; // Increase speed
			intervalId = window.setInterval(step, delay);
		}
	};

	element.innerHTML = String(start); // Set initial value
	intervalId = window.setInterval(step, delay);
}
